import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Dialog from '@radix-ui/react-dialog';
import { api } from '@/api';
import HelpDot, { preventDialogDismissForHelpPop } from '@/components/help/HelpDot';
import { getInlineTipForContext } from '@/data/help-inline-tips';

import './edit-family-name-modal.css';

interface EditFamilyNameModalProps {
  familyId: string;
  currentDisplayName: string;
  canonicalName: string;
  hasAlternative: boolean;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (displayName: string, alternativeName: string | null) => void;
}

const EditFamilyNameModal: React.FC<EditFamilyNameModalProps> = ({
  familyId,
  currentDisplayName,
  canonicalName,
  hasAlternative,
  isOpen,
  onClose,
  onSuccess
}) => {
  const { t } = useTranslation('forms');
  const { t: tCommon } = useTranslation('common');
  const [value, setValue] = useState(currentDisplayName);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const editFamilyNameHelpTip = getInlineTipForContext('edit-family-name-modal');

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      setValue(currentDisplayName);
      setErrorMessage(null);
    }
  }, [isOpen, currentDisplayName]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    const trimmed = value.trim();
    if (!trimmed) {
      setErrorMessage(t('editFamilyName.nameRequired'));
      return;
    }
    if (trimmed.length > 255) {
      setErrorMessage(t('editFamilyName.nameTooLong'));
      return;
    }
    setIsSubmitting(true);
    try {
      if (trimmed === canonicalName) {
        const result = await api.families.deleteDisplayName(familyId);
        if (result.success) {
          onSuccess(result.data.displayName, null);
          onClose();
        } else {
          setErrorMessage(result.error.getUserMessage());
        }
      } else {
        const result = await api.families.setDisplayName(familyId, { alternativeName: trimmed });
        if (result.success) {
          onSuccess(result.data.displayName, result.data.alternativeName ?? trimmed);
          onClose();
        } else {
          setErrorMessage(result.error.getUserMessage());
        }
      }
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : t('editFamilyName.errorGeneric'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClear = async () => {
    setErrorMessage(null);
    setIsSubmitting(true);
    try {
      const result = await api.families.deleteDisplayName(familyId);
      if (result.success) {
        setValue(canonicalName);
        onSuccess(result.data.displayName, null);
        if (!hasAlternative) onClose();
      } else {
        setErrorMessage(result.error.getUserMessage());
      }
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : t('editFamilyName.errorGeneric'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="v01-modal-overlay v01-modal-overlay--centered">
          <Dialog.Content
            className="v01-modal v01-modal--panel"
            aria-labelledby="edit-family-name-title"
            aria-describedby="edit-family-name-desc"
            onPointerDownOutside={preventDialogDismissForHelpPop}
            onFocusOutside={preventDialogDismissForHelpPop}
          >
            <div className="v01-modal__header v01-modal__header--compact">
              <h2 id="edit-family-name-title" className="v01-modal__title">
                {t('editFamilyName.title')}
              </h2>
              {editFamilyNameHelpTip && (
                <HelpDot
                  title={editFamilyNameHelpTip.title}
                  body={editFamilyNameHelpTip.body}
                  storageKey={editFamilyNameHelpTip.storageKey}
                  docId="custom-family-name"
                />
              )}
            </div>
            <p id="edit-family-name-desc" className="v01-modal__desc">
              {t('editFamilyName.description')}
            </p>
            <form onSubmit={handleSubmit}>
              <label htmlFor="family-display-name" className="v01-modal__label">
                {t('editFamilyName.label')}
              </label>
              <input
                id="family-display-name"
                type="text"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={canonicalName}
                maxLength={255}
                className="v01-modal__input"
                autoFocus
                disabled={isSubmitting}
              />
              {errorMessage && (
                <div className="v01-modal__error" role="alert">
                  <span>{errorMessage}</span>
                  <button
                    type="button"
                    onClick={() => setErrorMessage(null)}
                    className="v01-modal__error-dismiss"
                    aria-label={t('actions.dismiss')}
                  >
                    ×
                  </button>
                </div>
              )}
              <div className="edit-family-name-modal-actions">
                {hasAlternative && (
                  <button type="button" onClick={handleClear} className="btn btn--text" disabled={isSubmitting}>
                    {t('actions.useOriginalName')}
                  </button>
                )}
                <div className="edit-family-name-modal-actions-primary">
                  <button type="button" onClick={onClose} className="btn btn--ghost" disabled={isSubmitting}>
                    {tCommon('cancel')}
                  </button>
                  <button type="submit" className="btn btn--primary" disabled={isSubmitting}>
                    {isSubmitting ? tCommon('saving') : t('actions.save')}
                  </button>
                </div>
              </div>
            </form>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default EditFamilyNameModal;
